import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { AuthContext } from '../Providers/AuthContext';
import { FriendsContext } from '../Providers/FriendsContext';
import { useLanguage, LANGUAGES, LanguageCode } from '../Providers/LanguageContext';
import { isAge16OrOlder } from '../../utils/ageUtils';
import DOBCollectionModal from '../DOBCollectionModal/DOBCollectionModal';
import BugReportModal from '../BugReportModal/BugReportModal';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import styles from './GlobalHeader.module.css';
import { playUISound } from '../../utils/UISoundEffects';

// Volume states enum
enum VolumeState {
  Off = 'off',
  Mid = 'mid',
  High = 'high'
}

interface GlobalHeaderProps {
  showVolumeControl?: boolean;
  showLogo?: boolean;
  showBackButton?: boolean;
  showPlayerOptions?: boolean;
  backUrl?: string;
  position?: 'absolute' | 'relative';
  className?: string;
}

function GlobalHeader({
  showVolumeControl = true,
  showLogo = false,
  showBackButton = false,
  showPlayerOptions = true,
  backUrl = "/",
  position = 'absolute',
  className = ''
}: GlobalHeaderProps) {
  const [volumeState, setVolumeState] = useState<VolumeState>(VolumeState.Off);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showLanguageSubmenu, setShowLanguageSubmenu] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showDOBModal, setShowDOBModal] = useState(false);
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const { isPremium, accountType, user, userProfile, updateProfile } = useContext(AuthContext);
  const { unreadCount, anyFriendOnline, toggleSidebar } = useContext(FriendsContext);
  const { language, setLanguage, t } = useLanguage();

  // Load hover sound
  useEffect(() => {
    const loadSound = async () => {
      try {
        const context = new AudioContext();
        audioContextRef.current = context;
        const response = await fetch('/on-001-337979.mp3');
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await context.decodeAudioData(arrayBuffer);
        audioBufferRef.current = audioBuffer;
      } catch (e) {
        // Ignore audio load errors
      }
    };
    loadSound();
  }, []);

  // Play hover sound - dulled with low-pass filter
  const playHoverSound = useCallback(() => {
    if (audioContextRef.current && audioBufferRef.current) {
      const context = audioContextRef.current;
      const source = context.createBufferSource();
      source.buffer = audioBufferRef.current;

      const filter = context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 400;

      const gainNode = context.createGain();
      gainNode.gain.value = 0.02;

      source.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(context.destination);
      source.start(0);
    }
  }, []);

  // Load volume state from localStorage on mount, default to Off for first-time visitors
  useEffect(() => {
    const savedVolume = localStorage.getItem('zodiac-volume-state');
    if (savedVolume && Object.values(VolumeState).includes(savedVolume as VolumeState)) {
      // User has a saved preference - use it
      setVolumeState(savedVolume as VolumeState);
    } else {
      // First-time visitor - default to Off (no auto-play)
      setVolumeState(VolumeState.Off);
    }
    setHasInitialized(true);
  }, []);

  // Save volume state to localStorage whenever it changes
  useEffect(() => {
    if (hasInitialized) {
      localStorage.setItem('zodiac-volume-state', volumeState);
    }
  }, [volumeState, hasInitialized]);

  // Initialize audio and handle autoplay for first-time visitors
  useEffect(() => {
    if (!hasInitialized || !audioRef.current) return;

    if (volumeState === VolumeState.Mid) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {
        // Autoplay blocked by browser - that's ok
      });
    } else if (volumeState === VolumeState.High) {
      audioRef.current.volume = 0.6;
      audioRef.current.play().catch(() => {
        // Autoplay blocked by browser - that's ok
      });
    }
  }, [hasInitialized]);

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    };

    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu]);

  // Function to cycle through volume states
  const cycleVolumeState = () => {
    if (volumeState === VolumeState.Off) {
      setVolumeState(VolumeState.Mid);
      if (audioRef.current) {
        audioRef.current.volume = 0.3; // Low volume
        audioRef.current.play().catch(error => {
        });
      }
    } else if (volumeState === VolumeState.Mid) {
      setVolumeState(VolumeState.High);
      if (audioRef.current) {
        audioRef.current.volume = 0.6; // Medium volume
        audioRef.current.play().catch(error => {
        });
      }
    } else {
      setVolumeState(VolumeState.Off);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  // Get volume icon based on current state
  const getVolumeIcon = () => {
    switch (volumeState) {
      case VolumeState.Off:
        return '/Icons/0vol.png';
      case VolumeState.Mid:
        return '/Icons/midvol.png';
      case VolumeState.High:
        return '/Icons/highvol.png';
      default:
        return '/Icons/0vol.png';
    }
  };

  // Handle chat toggle
  const handleToggleChat = async () => {
    if (!userProfile || accountType === 'temp') return;

    const newChatEnabled = !(userProfile.chatEnabled ?? true);
    await updateProfile({
      ...userProfile,
      chatEnabled: newChatEnabled
    });
  };

  // Handle Stripe customer portal redirect
  const handleManageSubscription = async () => {
    if (!user || !isPremium) {
      return;
    }

    // For now, redirect to Stripe billing portal
    // In production, you should create a portal session via your backend
    // and get the URL dynamically
    const STRIPE_PORTAL_URL = 'https://billing.stripe.com/p/login/test_bJecMXcxv6kP1tGf2w14400';


    window.open(STRIPE_PORTAL_URL, '_blank');
  };

  return (
    <>
      <div className={`${styles.globalHeader} ${position === 'relative' ? styles.relative : ''} ${className}`}>
        {showLogo && (
          <img src="/images/zodiaclog.png" alt="Zodiac Logo" className={styles.logoImage} />
        )}
        <div className={styles.headerControls}>
          <ThemeToggle />
          {user && accountType === 'real' && (
            <button
              type="button"
              className={styles.friendsButton}
              onClick={toggleSidebar}
              aria-label="Friends"
            >
              <svg className={styles.friendsIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              {unreadCount > 0 ? (
                <span className={styles.friendsBadge}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              ) : anyFriendOnline ? (
                <span className={styles.friendsOnlineDot} />
              ) : null}
            </button>
          )}
          {showPlayerOptions && (
            <div className={styles.settingsContainer} ref={settingsMenuRef}>
              <button
                type="button"
                className={styles.playerOptionsButton}
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                aria-label="Player Options"
              >
              </button>

              {showSettingsMenu && (
                <div className={styles.settingsMenu}>
                  <div className={styles.settingsMenuHeader}>{t('settings.header')}</div>

                  <Link
                    to="/main"
                    className={styles.settingsMenuItem}
                    onClick={() => setShowSettingsMenu(false)}
                    onMouseEnter={playHoverSound}
                  >
                    <span>{t('settings.home')}</span>
                  </Link>

                  {isPremium && accountType === 'real' && (
                    <>
                      <div className={styles.settingsMenuDivider}></div>
                      <div className={styles.settingsMenuSection}>{t('settings.premiumAccount')}</div>

                      <button
                        className={styles.settingsMenuItem}
                        onClick={() => {
                          handleManageSubscription();
                          setShowSettingsMenu(false);
                        }}
                        onMouseEnter={playHoverSound}
                      >
                        <span>{t('settings.manageSubscription')}</span>
                      </button>
                    </>
                  )}

                  <div className={styles.settingsMenuDivider}></div>

                  <button
                    className={styles.settingsMenuItem}
                    onClick={() => setShowLanguageSubmenu(!showLanguageSubmenu)}
                    onMouseEnter={playHoverSound}
                  >
                    <span>{t('settings.language')}: {LANGUAGES[language].nativeName}</span>
                    <span className={styles.submenuArrow}>{showLanguageSubmenu ? '▼' : '▶'}</span>
                  </button>

                  {showLanguageSubmenu && (
                    <div className={styles.languageSubmenu}>
                      {Object.entries(LANGUAGES).map(([code, lang]) => (
                        <button
                          key={code}
                          className={`${styles.languageMenuItem} ${language === code ? styles.activeLanguage : ''}`}
                          onClick={() => {
                            setLanguage(code as LanguageCode);
                            setShowLanguageSubmenu(false);
                          }}
                          onMouseEnter={playHoverSound}
                        >
                          <span className={styles.languageNative}>{lang.nativeName}</span>
                          <span className={styles.languageEnglish}>({lang.name})</span>
                          {language === code && <span className={styles.checkmark}>✓</span>}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Chat toggle for 16+ real accounts only */}
                  {accountType === 'real' && isAge16OrOlder(userProfile?.dateOfBirth) && (
                    <>
                      <div className={styles.settingsMenuDivider}></div>
                      <button
                        className={styles.settingsMenuItem}
                        onClick={handleToggleChat}
                        onMouseEnter={playHoverSound}
                      >
                        <span>{userProfile?.chatEnabled ?? true ? t('settings.chatEnabled') : t('settings.chatDisabled')}</span>
                        <span className={styles.checkmark}>{userProfile?.chatEnabled ?? true ? '✓' : ''}</span>
                      </button>
                    </>
                  )}

                  {/* DOB prompt for real accounts without DOB */}
                  {accountType === 'real' && !userProfile?.dateOfBirth && (
                    <>
                      <div className={styles.settingsMenuDivider}></div>
                      <button
                        className={styles.settingsMenuItem}
                        onClick={() => {
                          setShowDOBModal(true);
                          setShowSettingsMenu(false);
                        }}
                        onMouseEnter={playHoverSound}
                      >
                        <span>Add Date of Birth to enable chat</span>
                      </button>
                    </>
                  )}

                  {/* Legal Pages */}
                  <div className={styles.settingsMenuDivider}></div>
                  <div className={styles.settingsMenuSection}>Legal</div>

                  <Link
                    to="/privacy"
                    className={styles.settingsMenuItem}
                    onClick={() => setShowSettingsMenu(false)}
                    onMouseEnter={playHoverSound}
                  >
                    <span>Privacy Policy</span>
                  </Link>

                  <Link
                    to="/terms"
                    className={styles.settingsMenuItem}
                    onClick={() => setShowSettingsMenu(false)}
                    onMouseEnter={playHoverSound}
                  >
                    <span>Terms of Service</span>
                  </Link>

                  <Link
                    to="/cookies"
                    className={styles.settingsMenuItem}
                    onClick={() => setShowSettingsMenu(false)}
                    onMouseEnter={playHoverSound}
                  >
                    <span>Cookie Policy</span>
                  </Link>

                  <div className={styles.settingsMenuDivider}></div>

                  <button
                    className={styles.settingsMenuItem}
                    onClick={() => {
                      setShowFeedbackModal(true);
                      setShowSettingsMenu(false);
                    }}
                    onMouseEnter={playHoverSound}
                  >
                    <span>Feedback</span>
                  </button>

                  <button
                    className={styles.settingsMenuItem}
                    onClick={() => {
                      setShowBugReportModal(true);
                      setShowSettingsMenu(false);
                    }}
                    onMouseEnter={playHoverSound}
                  >
                    <span>Report a Bug</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {showVolumeControl && (
            <button 
              type="button"
              className={styles.volumeButton} 
              onClick={cycleVolumeState}
              aria-label={`Volume: ${volumeState}`}
            >
              <img 
                src={getVolumeIcon()} 
                alt={`Volume: ${volumeState}`} 
                className={styles.volumeIcon} 
              />
            </button>
          )}
          {showBackButton && (
            <Link to={backUrl} className={styles.backButton} onClick={playUISound}>
              BACK
            </Link>
          )}
        </div>
      </div>
      {showVolumeControl && <audio ref={audioRef} src="/zodiacfinal2.mp3" loop />}
      <DOBCollectionModal
        isOpen={showDOBModal}
        onClose={() => setShowDOBModal(false)}
      />
      <BugReportModal
        isOpen={showBugReportModal}
        onClose={() => setShowBugReportModal(false)}
      />
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </>
  );
}

export default GlobalHeader;
